import * as React from 'react';
import { Badge, ButtonDynamicContent, FilterButton, Tag, Typography } from 'omakase';
import { IconAdjust } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { IconRefine } from 'ui/icons';
import { LocalDate, LocalTime } from 'js-joda';
import { LocaleContext } from '@tock/coretypes';
import {
  NULL_PARTY_SIZE_LABEL,
  SearchChangeProps,
} from 'components/www/availability-search/SearchHeader';
import { SearchResultsOpenMode, useVisibleFilters } from 'components/search/utils';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { getAppliedFiltersCountForVisibleFilters } from 'utils/search/searchFiltersUtils';
import { getDisplayedTimes } from 'utils/timeUtils';
import {
  searchBarMobile,
  searchBarMobileContainer,
  searchBarMobileHeader,
} from 'components/www/availability-search/searchHeaderCssDefs';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import AdjustSearchDialog from 'components/www/availability-search/filters/AdjustSearchDialog';
import SearchFiltersDialog, {
  SearchFilterType,
} from 'components/www/availability-search/filters/SearchFiltersDialog';
import SearchFiltersDialogTrigger from 'components/www/availability-search/filters/SearchFiltersDialogTrigger';

type SearchBarMobileProps = SearchChangeProps;

const SearchBarMobile: React.FunctionComponent<SearchBarMobileProps> = ({
  date,
  dateEnd,
  q,
  size,
  time,
  type,
  collectionTagLabel,
  cuisines,
  experienceTags,
  priceRange,
  currentQ,
  currentSize,
  onChangeType,
  onChangeQ,
  onChangeSize,
  onChangeTime,
  onChangeDate,
  onChangeCuisines,
  onClearCollectionTag,
  onChangeSearchFilters,
  onSearch,
  onReset,
  dateLabel,
  experiencesLabel,
  guestsLabel,
  qLabel,
  timeLabel,
  header,
  startDate,
  endDate,
  sizeOptions,
  typeOptions,
  showTimeFilterLabel,
  onChangeDateRange,
}) => {
  const [openMode, setOpenMode] = React.useState<SearchResultsOpenMode>('CLOSED');
  const [searchLabel, setSearchLabel] = React.useState<string | undefined>(undefined);
  const searchContext = useSearchHandlerContext();
  const { mode, availability } = { ...searchContext };
  const visibleFilters = useVisibleFilters(mode, type);

  const locale = React.useContext(LocaleContext);

  const getSearchType = (openMode: SearchResultsOpenMode): string | undefined => {
    switch (openMode) {
      case 'TYPE_OPEN': {
        if (mode?.stateType === 'city') {
          return 'Select experience type';
        }
        break;
      }
      case 'TEXT_OPEN': {
        return 'Update search';
      }
      case 'SEARCH_OPEN': {
        if (mode?.stateType === 'togo' && visibleFilters.includes('DATE')) {
          return 'Select date';
        }
        if (
          mode?.stateType === 'city' &&
          visibleFilters.includes('DATE') &&
          visibleFilters.includes('SIZE') &&
          visibleFilters.includes('TIME')
        ) {
          return 'Adjust your search';
        }
        return 'Adjust your search';
      }
      case 'CUISINES_OPEN': {
        return;
      }
      case 'SEARCH_FILTERS_OPEN': {
        return;
      }
      case 'CLOSED': {
        return;
      }
      default:
        assertNever(openMode);
    }
    return;
  };

  const handleSetOpenMode = (openMode: SearchResultsOpenMode) => {
    const searchLabel = getSearchType(openMode);
    setOpenMode(openMode);
    setSearchLabel(searchLabel);
  };

  /**
   * reset back to current values
   */
  const handleCloseSearch = () => {
    setOpenMode('CLOSED');
    onReset();
  };

  const displayedTimes = date
    ? getDisplayedTimes(LocalDate.parse(date), LocalTime.now(), locale)
    : [];

  const isAdjustSearchDialogOpen = () => {
    switch (openMode) {
      case 'SEARCH_OPEN':
      case 'TEXT_OPEN':
      case 'TYPE_OPEN':
        return true;
      case 'CLOSED':
      case 'CUISINES_OPEN':
      case 'SEARCH_FILTERS_OPEN':
        return false;
      default:
        assertNever(openMode);
    }
  };

  const isRefineSearchDialogOpen = () => openMode === 'SEARCH_FILTERS_OPEN';

  const visibleRefineSearchDialogFilters: SearchFilterType[] = [
    'OFFERING_TYPE',
    'PRICE',
    'SEATING_AREA',
    'UNIQUE_EXPERIENCES',
    'CUISINES',
  ];

  const appliedFiltersCount = getAppliedFiltersCountForVisibleFilters(
    visibleRefineSearchDialogFilters,
    { priceRange, experienceTags, cuisines, type }
  );

  const showHeader = mode?.stateType === 'city' && mode?.queryType.DISPLAYTERM ? true : false;

  return (
    <div css={searchBarMobile}>
      {showHeader && (
        <div css={searchBarMobileHeader}>
          <Typography component="h1" variant="heading03">
            {header?.shortHeader}
          </Typography>
        </div>
      )}
      <div css={searchBarMobileContainer}>
        <FilterButton
          aria-haspopup
          aria-label="Edit date, time, and party size"
          onClick={() => handleSetOpenMode('SEARCH_OPEN')}
          data-testid="filter-mobile-button"
          selected={!!(dateLabel || timeLabel || guestsLabel)}
          fullWidth
        >
          {/* sync this up? with desktop? */}
          {visibleFilters.includes('DATE') && (
            <ButtonDynamicContent placeholder="Date">{dateLabel}</ButtonDynamicContent>
          )}
          {visibleFilters.includes('TIME') && showTimeFilterLabel && (
            <ButtonDynamicContent placeholder="Time">{timeLabel}</ButtonDynamicContent>
          )}
          {visibleFilters.includes('SIZE') && (
            <ButtonDynamicContent placeholder={NULL_PARTY_SIZE_LABEL}>
              {currentSize ? guestsLabel : ''}
            </ButtonDynamicContent>
          )}
        </FilterButton>

        <Badge size="medium" invisible={appliedFiltersCount === 0}>
          <SearchFiltersDialogTrigger
            onOpen={() => setOpenMode('SEARCH_FILTERS_OPEN')}
            triggerComponent={
              <FilterButton data-testid="mobile-search-filter-trigger" iconOnly>
                <IconMigrationUtil legacyIcon={<IconRefine />} newIcon={<IconAdjust />} />
              </FilterButton>
            }
            appliedFiltersCount={appliedFiltersCount}
          />
        </Badge>

        {visibleFilters.includes('TEXT') && currentQ && (
          <FilterButton
            aria-haspopup
            aria-label="Edit search term"
            onClick={() => handleSetOpenMode('TEXT_OPEN')}
            selected={!!qLabel}
            data-testid="search-term-edit-button"
          >
            {qLabel}
          </FilterButton>
        )}

        {visibleFilters.includes('TAG') && collectionTagLabel && (
          <Tag onDelete={onClearCollectionTag} variant="highContrast">
            {collectionTagLabel}
          </Tag>
        )}
      </div>
      <AdjustSearchDialog
        isAdjustSearchDialogOpen={isAdjustSearchDialogOpen}
        handleCloseSearch={handleCloseSearch}
        searchLabel={searchLabel}
        visibleFilters={visibleFilters}
        openMode={openMode}
        size={size}
        onChangeSize={onChangeSize}
        sizeOptions={sizeOptions}
        dateEnd={dateEnd}
        time={time}
        onChangeTime={onChangeTime}
        displayedTimes={displayedTimes}
        type={type}
        onChangeType={onChangeType}
        typeOptions={typeOptions}
        startDate={startDate}
        endDate={endDate}
        date={date}
        onChangeDate={onChangeDate}
        q={q}
        onChangeQ={onChangeQ}
        setOpenMode={setOpenMode}
        onSearch={onSearch}
        onChangeDateRange={onChangeDateRange}
      />
      <SearchFiltersDialog
        isOpen={isRefineSearchDialogOpen}
        onClose={() => setOpenMode('CLOSED')}
        dialogTitle="Refine your search"
        selectedCuisines={cuisines}
        selectedType={type}
        selectedPriceRange={priceRange}
        selectedExperienceTags={experienceTags}
        onSubmit={onChangeSearchFilters}
        aggregations={availability?.result?.aggregations || undefined}
        visibleSearchFilters={visibleRefineSearchDialogFilters}
      />
    </div>
  );
};

export default SearchBarMobile;
